import React, { useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { monthYearPresetParamLabel } from '../utils/monthYearPreset';

const FILTER_MENU_Z_INDEX = 1700;
const SEARCH_AFTER = 8;

function optionLabel(opt) {
  if (!opt) return '';
  return opt.label != null ? String(opt.label) : String(opt.value ?? '');
}

/** «clase» → «Clase», «time_ventana» → «Time ventana». Los 6 rangos de fecha conservan su etiqueta. */
export function humanizeFilterParam(paramName) {
  const preset = monthYearPresetParamLabel(paramName);
  if (preset) return preset;
  const raw = String(paramName || '').replace(/^@/, '').trim();
  if (!raw) return '';
  const spaced = raw.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function isTimeWindowFilter(df) {
  return df?.filterKind === 'timeRange' || df?.filterKind === 'relativeTime';
}

/** Etiqueta visible. Los filtros de ventana de tiempo no llevan nombre: van con el ícono de reloj. */
export function labelForFilterParam(paramName, df, aliases) {
  if (isTimeWindowFilter(df)) return '';
  const alias = aliases?.[paramName];
  if (alias != null && String(alias).trim()) return String(alias).trim();
  return humanizeFilterParam(paramName);
}

/** Valor de un filtro dinámico para mostrar en un chip de solo lectura (ej. filtro global). */
export const formatDynamicFilterChipValue = (value) => {
  if (value == null || value === '') return '—';
  const str = String(value);
  // Cubre tanto una fecha con hora ("...T14:30:00Z") como una fecha sola
  // ("2026-09-27", lo que devuelve un input type="date"); antes solo
  // reconocía la primera forma y dejaba la segunda sin reformatear.
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z)?)?$/);
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    return `${dd}-${mm}-${yyyy}`;
  }
  return str;
};

function valuesDiffer(current, fallback) {
  if (fallback == null || String(fallback).trim() === '') return false;
  return String(current ?? '') !== String(fallback);
}

const pillSx = (theme, { changed, time, open, disabled }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  height: 28,
  maxWidth: '100%',
  px: 1.25,
  borderRadius: 999,
  border: '1px solid',
  borderColor: open || changed ? 'primary.main' : 'divider',
  bgcolor: changed ? 'action.selected' : time ? 'action.hover' : 'background.paper',
  color: 'text.primary',
  fontSize: 13,
  lineHeight: 1,
  fontFamily: theme.typography.fontFamily,
  m: 0,
  boxSizing: 'border-box',
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.55 : 1,
  whiteSpace: 'nowrap',
  '&:hover': disabled
    ? {}
    : { bgcolor: changed ? 'action.selected' : 'action.hover' },
});

function PillFace({ label, value, placeholder, time, caret }) {
  return (
    <>
      {time ? <AccessTimeIcon sx={{ fontSize: 15, color: 'text.secondary' }} /> : null}
      {label ? (
        <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {label}:
        </Box>
      ) : null}
      <Box
        component="span"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 220,
          color: value ? 'text.primary' : 'text.secondary',
        }}
      >
        {value || placeholder}
      </Box>
      {caret ? (
        <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary', ml: -0.25 }} />
      ) : null}
    </>
  );
}

function OptionList({ options, selected, onPick, query, onQuery, fullScreen = false }) {
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => optionLabel(opt).toLowerCase().includes(q));
  }, [options, query]);

  if (fullScreen) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {options.length > 6 && (
          <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Buscar…"
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
        <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
          {filtered.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
              Sin opciones
            </Typography>
          )}
          {filtered.map((opt) => {
            const isSelected = selected && String(selected.value) === String(opt.value);
            return (
              <ListItemButton
                key={opt._key ?? String(opt.value)}
                selected={isSelected}
                onClick={() => onPick(opt)}
                sx={{ minHeight: 52, py: 1.25, borderBottom: 1, borderColor: 'divider' }}
              >
                <ListItemText
                  primary={optionLabel(opt)}
                  primaryTypographyProps={{ fontWeight: isSelected ? 700 : 400, fontSize: '0.95rem' }}
                />
                {isSelected ? <CheckIcon color="primary" fontSize="small" /> : null}
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 180, maxWidth: 320 }}>
      {options.length > SEARCH_AFTER && (
        <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Buscar…"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}
      <List dense sx={{ maxHeight: 240, overflow: 'auto', py: 0.5 }}>
        {filtered.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
            Sin opciones
          </Typography>
        )}
        {filtered.map((opt) => {
          const isSelected = selected && String(selected.value) === String(opt.value);
          return (
            <ListItemButton
              key={opt._key ?? String(opt.value)}
              selected={isSelected}
              onClick={() => onPick(opt)}
              sx={{ py: 0.4, mx: 0.5, borderRadius: 1 }}
            >
              <ListItemText
                primary={optionLabel(opt)}
                primaryTypographyProps={{ fontSize: 13, fontWeight: isSelected ? 600 : 400 }}
              />
              {isSelected ? <CheckIcon color="primary" sx={{ fontSize: 16 }} /> : null}
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
}

function FilterMenuDialog({ open, onClose, label, title, options, selected, onPick }) {
  const [query, setQuery] = useState('');
  const displayValue = selected ? optionLabel(selected) : '';

  const handleClose = () => {
    setQuery('');
    onClose();
  };

  return (
    <Dialog fullScreen open={open} onClose={handleClose} sx={{ zIndex: FILTER_MENU_Z_INDEX }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25, pr: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {title || label || 'Filtro'}
          </Typography>
          {selected && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              Actual: {displayValue}
            </Typography>
          )}
        </Box>
        <IconButton aria-label="Cerrar" onClick={handleClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <OptionList
          fullScreen
          options={options}
          selected={selected}
          query={query}
          onQuery={setQuery}
          onPick={(opt) => {
            onPick(opt);
            handleClose();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

/** Píldora de solo lectura: mismo dibujo que el selector, sin menú. */
export function FilterValuePill({ label, value, time = false, title }) {
  return (
    <Box component="span" title={title} sx={(theme) => pillSx(theme, { time, disabled: false })}>
      <PillFace label={label} value={value || '—'} time={time} caret={false} />
    </Box>
  );
}

/** Fecha en la misma píldora que el resto de filtros. */
export function FilterDatePill({ label, value, onChange, defaultValue, disabled = false }) {
  const shown = (value ?? '').toString().slice(0, 10);
  const changed = valuesDiffer(shown, defaultValue == null ? '' : String(defaultValue).slice(0, 10));
  return (
    <Box
      component="label"
      sx={(theme) => pillSx(theme, { changed, disabled })}
    >
      {label ? (
        <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {label}:
        </Box>
      ) : null}
      <Box
        component="input"
        type="date"
        disabled={disabled}
        aria-label={label || 'Fecha'}
        value={shown}
        onChange={(e) => onChange?.(e.target.value)}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          border: 0,
          background: 'transparent',
          color: 'inherit',
          font: 'inherit',
          outline: 'none',
          width: 118,
          p: 0,
        }}
      />
    </Box>
  );
}

/**
 * Filtro dinámico en píldora. En móvil/embed abre el listado a pantalla completa;
 * en escritorio, un menú corto bajo la píldora.
 */
export default function DynamicFilterSelect({
  options = [],
  value = null,
  onChange,
  label,
  placeholder = 'Seleccionar…',
  disabled = false,
  preferNativeSelect = false,
  time = false,
  defaultValue,
}) {
  const [anchor, setAnchor] = useState(null);
  const [query, setQuery] = useState('');
  const open = Boolean(anchor);
  const selected =
    value && typeof value === 'object'
      ? value
      : (options || []).find((o) => String(o.value) === String(value)) ?? null;
  const inactive = disabled || !options.length;
  const changed = valuesDiffer(selected ? selected.value : value, defaultValue);
  const display = selected ? optionLabel(selected) : '';

  const close = () => {
    setAnchor(null);
    setQuery('');
  };

  const pick = (opt) => {
    onChange(opt ?? null);
    close();
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        disabled={inactive}
        aria-label={label ? `${label}: ${display || placeholder}` : display || placeholder}
        aria-expanded={open}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          if (inactive) return;
          setAnchor(e.currentTarget);
        }}
        sx={(theme) => pillSx(theme, { changed, time, open, disabled: inactive })}
      >
        <PillFace
          label={label}
          value={display}
          placeholder={placeholder}
          time={time}
          caret
        />
      </Box>

      {preferNativeSelect ? (
        <FilterMenuDialog
          open={open}
          onClose={close}
          label={label}
          title={label || (time ? 'Ventana de tiempo' : 'Filtro')}
          options={options}
          selected={selected}
          onPick={pick}
        />
      ) : (
        <Popover
          open={open}
          anchorEl={anchor}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{ zIndex: FILTER_MENU_Z_INDEX }}
          PaperProps={{ sx: { mt: 0.5 } }}
        >
          <OptionList
            options={options}
            selected={selected}
            query={query}
            onQuery={setQuery}
            onPick={pick}
          />
        </Popover>
      )}
    </>
  );
}

export function shouldPreferNativeFilterSelect({ embedMode = false, narrowViewport = false } = {}) {
  if (embedMode || narrowViewport) return true;
  if (typeof window === 'undefined') return false;
  return Boolean(
    window.matchMedia?.('(pointer: coarse)')?.matches || navigator.maxTouchPoints > 0
  );
}
